import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  createFeedbackRequestBodySchema,
  EXPIRATION_DAY_OPTIONS,
  PRODUCT_TYPES,
  type CreatedFeedbackRequest,
  type ExpirationDays,
  type ProductTypeCode,
} from '@fois-utsavvoice/shared';
import { createFeedbackRequest } from '../api/admin.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';
import { QrCard } from '../components/QrCard.js';

export function AdminCreateRequestPage() {
  const auth = useAuth();
  const [orderReference, setOrderReference] = useState('');
  const [productType, setProductType] = useState<ProductTypeCode>('GANESH_POOJA_BOX');
  const [productName, setProductName] = useState<string>(PRODUCT_TYPES[0].displayName);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [expirationDays, setExpirationDays] = useState<ExpirationDays>(90);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [created, setCreated] = useState<CreatedFeedbackRequest | undefined>();
  const [copied, setCopied] = useState(false);

  function onProductTypeChange(code: ProductTypeCode): void {
    setProductType(code);
    const match = PRODUCT_TYPES.find((item) => item.code === code);
    if (match) {
      setProductName(match.displayName);
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.tokens) {
      return;
    }
    const parsed = createFeedbackRequestBodySchema.safeParse({
      orderReference,
      productType,
      productName,
      customerName,
      customerEmail,
      expirationDays,
    });
    if (!parsed.success) {
      setError('Check the order number and product details, then try again.');
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const result = await createFeedbackRequest(auth.tokens.idToken, parsed.data);
      setCreated(result);
    } catch {
      setError('The feedback request could not be created.');
    } finally {
      setSubmitting(false);
    }
  }

  const customerUrl = created ? `${window.location.origin}${created.feedbackPath}` : '';

  return (
    <PageShell>
      <AdminNav />
      {created ? (
        <section className="card mx-auto max-w-2xl">
          <h1 className="heading-xl text-crimson">Feedback request created</h1>
          <p className="mt-3 text-charcoal">
            Add this unique link to the Shopify invoice, email, WhatsApp message, or thank-you card.
            The token is shown only once.
          </p>
          <label className="field-label mt-6" htmlFor="customer-url">
            Customer URL
          </label>
          <textarea
            id="customer-url"
            className="field-input"
            readOnly
            rows={3}
            value={customerUrl}
          />
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              className="btn-primary"
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(customerUrl);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <a
              className="btn-secondary"
              href={created.feedbackPath}
              target="_blank"
              rel="noreferrer"
            >
              Open customer form
            </a>
            <Link className="btn-secondary" to={`/admin/requests/${created.requestId}`}>
              View request
            </Link>
          </div>
          <QrCard url={customerUrl} />
        </section>
      ) : (
        <section className="card mx-auto max-w-2xl">
          <h1 className="heading-xl text-crimson">Create feedback request</h1>
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="field-label" htmlFor="orderReference">
                Shopify order number
              </label>
              <input
                id="orderReference"
                required
                className="field-input"
                value={orderReference}
                onChange={(event) => setOrderReference(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="productType">
                Box or product type
              </label>
              <select
                id="productType"
                className="field-input"
                value={productType}
                onChange={(event) => onProductTypeChange(event.target.value as ProductTypeCode)}
              >
                {PRODUCT_TYPES.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="productName">
                Display name
              </label>
              <input
                id="productName"
                required
                className="field-input"
                value={productName}
                onChange={(event) => setProductName(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="customerName">
                Customer name (optional)
              </label>
              <input
                id="customerName"
                className="field-input"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="customerEmail">
                Customer email (optional)
              </label>
              <input
                id="customerEmail"
                type="email"
                className="field-input"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
              />
            </div>
            <fieldset>
              <legend className="field-label">Link expiration</legend>
              <div className="flex flex-wrap gap-2">
                {EXPIRATION_DAY_OPTIONS.map((days) => (
                  <label
                    key={days}
                    className={`choice-chip ${
                      expirationDays === days ? 'choice-chip-selected' : ''
                    }`}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="expirationDays"
                      value={days}
                      checked={expirationDays === days}
                      onChange={() => setExpirationDays(days)}
                    />
                    {days} days
                  </label>
                ))}
              </div>
            </fieldset>
            {error ? (
              <p className="text-sm font-semibold text-crimson" role="alert">
                {error}
              </p>
            ) : null}
            <button className="btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create feedback request'}
            </button>
          </form>
        </section>
      )}
    </PageShell>
  );
}
